import { useEffect, memo, CSSProperties } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectValidParams, selectGridData } from '@pages/reporting-pages/redated-orders/store/selectors';
import { createGridData } from '@pages/reporting-pages/redated-orders/store/slice';
import { useLazyRedatedOrdersQuery } from '@api/order-reporting/api';
import { GridLoading, GridError, GridEmpty, GridNoParams } from 'components/global/GridElements';
import { useGridSorting } from 'components/filtering/filter-logic/useGridSorting';
import { useGridFiltering } from 'components/filtering/filter-logic/useGridFiltering';
import GridHeader from '@pages/reporting-pages/redated-orders/grid/GridHeader';
import GridSummary from '@pages/reporting-pages/redated-orders/grid/GridSummary';
import GridRow from '@pages/reporting-pages/redated-orders/grid/GridRow';
import { RedatedOrdersReport } from '@api/order-reporting/types/RedatedOrdersReport';
import { headersObject } from '@pages/reporting-pages/redated-orders/headersObject';



const Grid = () => {

	const validParams = useSelector(selectValidParams);
	const gridData = useSelector(selectGridData);
	const [ runQuery, { data, isLoading, isFetching, isError, isSuccess } ] = useLazyRedatedOrdersQuery();
	const [ filteredData, filterObject, handleSearchValues, handleAddFilterValues, clearFilter ] = useGridFiltering(gridData);
	const [ sortedData, sortObject, handleSorting, clearSorting ] = useGridSorting(filteredData);

	const dispatch = useDispatch();
	const runCreateGridData = (data: RedatedOrdersReport[] | undefined | null) => dispatch(createGridData(data));

	const className = 'app-grid redated-orders-grid';
	const style: CSSProperties = {
		gridTemplateColumns: headersObject.map(x => x.width).join(' '),
	}

	
	useEffect(() => {
		if (validParams) {
			runQuery({ ...validParams }, true);
		}
	}, [ validParams ]);


	useEffect(() => {
		if (data) {
			runCreateGridData(data);
		}
	}, [ data ]);

	
	if (!validParams) return (
		<div className={className} style={style}>
			<GridHeader />
			<GridNoParams headersLength={headersObject.length} />
		</div>
	);

	if (isLoading || isFetching) return (
		<div className={className} style={style}>
			<GridHeader />
			<GridLoading headersLength={headersObject.length} />
		</div>
	);

	if (isError || !data) return (
		<div className={className} style={style}>
			<GridHeader />
			<GridError headersLength={headersObject.length} />
		</div>
	);
	
	if (isSuccess && gridData && gridData.length === 0) return (
		<div className={className} style={style}>
			<GridHeader />
			<GridEmpty headersLength={headersObject.length} />
		</div>
	);

	return (
		<div className={className} style={style}>
			
			<GridHeader
				filterObject={filterObject}
				sortObject={sortObject}
				onSearchValues={handleSearchValues}
				onAddFilterValues={handleAddFilterValues}
				onClearFilter={clearFilter}
				onSorting={handleSorting}
				onClearSorting={clearSorting}
			/>

			<GridSummary data={sortedData} />
			
			{sortedData && sortedData.map((x,i) =>
				<GridRow
					key={`id-redated-orders-grid-${x.orderUnversionedSystemId ? x.orderUnversionedSystemId : 'without-system-id'}`}
					item={x}
					rowIndex={i}
					validParams={validParams}
				/>
			)}
		</div>
	)
}

export default memo(Grid);